import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Dropdown from '../../components/common/Dropdown';
import { hostNameBack } from '../../utils/constants';
import Loader from '../../utils/Loader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import countries from "../../utils/countries.json"

function AdminRestrictedCountries() {
    const [restrictedCountries, setRestrictedCountries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCountry, setCurrentCountry] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [restrictions, setRestrictions] = useState({
        PayPal: false,
        Wise: false,
        Stripe: false,
        // Airwallex: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [countryToDelete, setCountryToDelete] = useState(null);

    const restrictionTypes = ['PayPal', 'Wise', 'Stripe'];

    useEffect(() => {
        fetchRestrictedCountries();
    }, []);

    const fetchRestrictedCountries = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${hostNameBack}/api/v1/settings/admin/restricted-countries`, {
                headers: { Authorization: `Admin ${token}` }
            });
            if (response.data.success) {
                setRestrictedCountries(response.data.restrictedCountries);
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || 'Failed to fetch restricted countries', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setSelectedCountry(countries[0].name);
        setRestrictions({
            PayPal: false,
            Wise: false,
            Stripe: false
        });
        setShowModal(true);
    };

    const handleOpenEditModal = (country) => {
        setIsEditing(true);
        setCurrentCountry(country.country);
        setSelectedCountry(country.country);
        const currentRestrictions = {};
        restrictionTypes.forEach(type => {
            currentRestrictions[type] = country.restrictions.includes(type);
        });
        setRestrictions(currentRestrictions);
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!selectedCountry && !isEditing) {
            enqueueSnackbar('Please select a country', { variant: 'warning' });
            return;
        }
        const selectedRestrictions = restrictionTypes.filter(type => restrictions[type]);
        if (selectedRestrictions.length === 0) {
            enqueueSnackbar('Please select at least one restriction', { variant: 'warning' });
            return;
        }
        setIsLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const data = { restrictions: selectedRestrictions };
            if (isEditing) {
                await axios.put(`${hostNameBack}/api/v1/settings/admin/restricted-countries/${encodeURIComponent(currentCountry)}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                data.country = selectedCountry;
                await axios.post(`${hostNameBack}/api/v1/settings/admin/restricted-countries`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            fetchRestrictedCountries();
            setShowModal(false);
            enqueueSnackbar(isEditing ? 'Country updated successfully' : 'Country added successfully', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || 'Something went wrong', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${hostNameBack}/api/v1/settings/admin/restricted-countries/${encodeURIComponent(countryToDelete)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRestrictedCountries();
            enqueueSnackbar('Country removed successfully', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || 'Failed to remove country', { variant: 'error' });
        } finally {
            setIsLoading(false);
            setDeleteDialogOpen(false);
            setCountryToDelete(null);
        }
    };

    const isSaveDisabled = (!isEditing && !selectedCountry) || !Object.values(restrictions).some(v => v);

    const tableRows = restrictedCountries.map((item, index) => (
        <tr key={index}>
            <td>{item.country}</td>
            <td>
                {item.restrictions.map((restriction, idx) => (
                    <span key={idx} className="restrictionBadge">{restriction}</span>
                ))}
            </td>
            <td>
                <FaEdit className="icon" title="Edit" onClick={() => handleOpenEditModal(item)} />
                <FaTrash className="icon" title="Delete" onClick={() => {
                    setCountryToDelete(item.country);
                    setDeleteDialogOpen(true);
                }} />
            </td>
        </tr>
    ));

    return (
        <div className="adminRestrictedCountriesDiv">
            {isLoading && <Loader type="checkmate" />}
            <div className="adminRestrictedCountriesContent">
                <div className='header'>
                    <h2 className="secondaryHeading"><span>Restricted</span> Countries</h2>
                    <button className="primaryBtn" onClick={handleOpenAddModal}>Add New Country</button>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Country</th>
                            <th>Restrictions</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.length > 0 ? tableRows : <tr><td colSpan="3">No restricted countries yet</td></tr>}
                    </tbody>
                </table>
            </div>
            {showModal && (
                <div className="modal">
                    <div className="modalContent">
                        <h3>{isEditing ? `Edit Restrictions for ${selectedCountry}` : `Add New Restricted Country`}</h3>
                        <div className="form-group">
                            <label>Country</label>
                            {isEditing ? (
                                <input type="text" value={selectedCountry} disabled className="inputField" />
                            ) : (
                                <Dropdown
                                    options={countries.map((country => country.name))}
                                    selected={selectedCountry}
                                    onSelect={setSelectedCountry}
                                    placeholder="Select Country"
                                />
                            )}
                        </div>
                        <div className="restrictions">
                            <p>Select Restrictions:</p>
                            {restrictionTypes.map(type => (
                                <label key={type}>
                                    <input
                                        type="checkbox"
                                        checked={restrictions[type]}
                                        onChange={(e) => setRestrictions({ ...restrictions, [type]: e.target.checked })}
                                    />
                                    {type}
                                </label>
                            ))}
                        </div>
                        <div className="buttons">
                            <button className="primaryBtn" onClick={handleSave} disabled={isSaveDisabled}>Save</button>
                            <button className="secondaryBtn" onClick={() => setShowModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete Country"
                message={`Are you sure you want to remove ${countryToDelete} from the restricted list?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialogOpen(false)}
                isLoading={isLoading}
            />
        </div>
    );
}

export default AdminRestrictedCountries;